/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import gql from 'graphql-tag';
import moment from 'moment';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { SHORT_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Link, Typography2 } from '@coursera/cds-core';

import withFragments from 'bundles/graphql/components/withFragments';
import imgixParams from 'bundles/page/common/imgixParams';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import type { S12nDataWithLaunchedAtDefined } from 'bundles/partners-graphql/components/Cards';

import _t from 'i18n!nls/phoenix-cdp';

const styles = {
  specializationCard: css`
    display: inline-block;
    min-height: 320px;
    padding: 5px 15px;
    vertical-align: top;
    width: 25%;

    img {
      height: auto;
      max-width: 100% !important; /* needed to override Imgix's style definition */
      width: 175px;

      &:hover {
        opacity: 0.8;
      }
    }

    @media (max-width: 480px) {
      text-align: center;
      width: 50%;
    }
  `,
  specializationName: css`
    margin: 8px 0 3px 0;
    color: #0062e4;
    max-width: 100% !important; /* needed to override Imgix's style definition */
    width: 175px;

    @media (max-width: 480px) {
      margin: 8px auto;
    }
  `,
};

type Props = {
  s12n: S12nDataWithLaunchedAtDefined;
};

const Specialization = ({ s12n }: Props) => {
  const getFormattedStartDate = () => {
    let startDateMessage;

    const momentLaunchedDate = moment(s12n.launchedAt);
    if (momentLaunchedDate > moment()) {
      const startDate = formatDateTimeDisplay(s12n.launchedAt, SHORT_DATE_ONLY_DISPLAY);
      startDateMessage = <FormattedMessage message={_t('Starts {startDate}')} startDate={startDate} />;
    } else {
      startDateMessage = _t('Available now');
    }
    return startDateMessage;
  };

  const s12nLink = '/specializations/' + s12n.slug;

  // dle TODO: Need to figure out why changing style file name doesn't update css
  return (
    <div css={styles.specializationCard}>
      <Link variant="standard" component={TrackedA} href={s12nLink} trackingName="link_to_sdp">
        <Imgix
          src={s12n.logo || ''}
          alt={s12n.name}
          maxWidth={175}
          maxHeight={175}
          imgParams={imgixParams.fillWithWhiteBackground}
          className="card-one-clicker"
        />
        <Typography2 css={styles.specializationName} variant="bodyPrimary" component="div">
          <FormattedMessage message={_t('{name} Specialization')} name={s12n.name} />
        </Typography2>
      </Link>
      <Typography2 component="p" variant="bodySecondary" color="supportText">
        {getFormattedStartDate()}
      </Typography2>
    </div>
  );
};

const specializationFragment = gql`
  fragment SpecializationFragment on OnDemandSpecializationsV1 {
    id
    name
    slug
    logo
    productVariant
    launchedAt
    courseIds
  }
`;

export default withFragments({ specializationFragment })(Specialization);
